// services/MP3EncoderService.ts - Simplified approach that avoids WAV conversion

interface EncoderConfig {
  sampleRate: number;
  bitRate: number;
  channels: number;
}

class MP3EncoderService {
  private defaultConfig: EncoderConfig = {
    sampleRate: 44100,
    bitRate: 128,
    channels: 1,
  };

  /**
   * "Convert" to MP3 by optimizing the existing audio format
   * This approach focuses on keeping files small rather than format conversion
   */
  async convertToMP3(
    audioBlob: Blob,
    config?: Partial<EncoderConfig>,
  ): Promise<Blob> {
    const finalConfig = { ...this.defaultConfig, ...config };

    console.log("🎵 Optimizing audio...", {
      originalSize: `${Math.round(audioBlob.size / 1024)} KB`,
      originalType: audioBlob.type,
    });

    try {
      // Check if the audio is already in a good compressed format and reasonable size
      if (this.isGoodFormat(audioBlob) && audioBlob.size <= 10 * 1024 * 1024) {
        console.log("✅ Audio format and size are already optimal");
        return audioBlob;
      }

      // For large files or non-optimal formats, return original
      // Avoid compression that might create incompatible formats
      if (audioBlob.size > 10 * 1024 * 1024) {
        console.log(
          "⚠️ Large audio file detected (",
          Math.round(audioBlob.size / 1024 / 1024),
          "MB)",
        );
        console.log(
          "💡 Tip: For best results, use compressed formats like MP3 or M4A",
        );
      }

      // For smaller files or if compression failed, return original
      console.log("✅ Using original audio file");
      return audioBlob;
    } catch (error) {
      console.warn(
        "⚠️ Audio optimization failed, using original:",
        error instanceof Error ? error.message : String(error),
      );
      return audioBlob;
    }
  }

  /**
   * Check if the audio format is already good for uploading
   */
  private isGoodFormat(blob: Blob): boolean {
    const goodFormats = [
      "audio/mpeg",
      "audio/mp3",
      "audio/mp4",
      "audio/m4a",
      "audio/webm",
    ];

    return goodFormats.some((format) => blob.type.includes(format));
  }

  /**
   * Validate audio blob
   */
  validateAudioBlob(blob: Blob): { isValid: boolean; error?: string } {
    if (!blob || blob.size === 0) {
      return { isValid: false, error: "Audio blob is empty" };
    }

    // Check file size (max 200MB)
    const maxSize = 200 * 1024 * 1024;
    if (blob.size > maxSize) {
      return {
        isValid: false,
        error: `Audio file is too large (${Math.round(
          blob.size / 1024 / 1024,
        )}MB). Maximum size is 200MB.`,
      };
    }

    return { isValid: true };
  }

  /**
   * Check if the browser supports the required APIs
   */
  isSupported(): boolean {
    return !!(window.AudioContext || (window as any).webkitAudioContext);
  }
}

// Export singleton instance
export default new MP3EncoderService();
